<template>
  <div class="space-y-2">
    <label
      v-if="label"
      :for="id"
      data-testid="phone-number-label"
      class="text-sm font-medium text-gray-700"
    >
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>
    <IconField>
      <InputIcon class="!leading-none !left-3">+{{ countryCode() }}</InputIcon>
      <InputMask
        :id="id"
        :key="inputMaskKey"
        v-model="userDisplay"
        data-testid="phone-number-input"
        mask="(999) 999-9999"
        :unmask="true"
        :auto-clear="false"
        type="tel"
        :placeholder="placeholder"
        :disabled="disabled"
        :class="['w-full !pl-[30px]', { 'p-invalid': v$.phone.$error }]"
        :aria-invalid="v$.phone.$error || undefined"
        :aria-describedby="v$.phone.$error ? errorId : undefined"
        @blur="onBlur"
        @complete="onComplete"
      />
    </IconField>
    <small
      v-if="v$.phone.$error"
      :id="errorId"
      data-testid="phone-number-error"
      role="alert"
      class="text-red-500 block"
    >
      {{ v$.phone.$errors[0]?.$message }}
    </small>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, useId, watch } from "vue";
import InputMask from "primevue/inputmask";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import useVuelidate from "@vuelidate/core";
import { required as requiredRule, helpers } from "@vuelidate/validators";
import {
  castOnValidPhoneNumber,
  isValidPhoneNumber,
  normalizePhone,
  phoneNumberErrorMessage,
  phoneNumberNoCodeLength,
  countryCode,
  stripCountryCode,
  type NANPPhoneNumber,
} from "@/shared/utils/phone-number";

interface Props {
  id?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  componentInputInvalidMessagePrefix?: string;
}

const props = withDefaults(defineProps<Props>(), {
  id: () => useId() ?? "",
  label: "",
  placeholder: "(555) 123-4567",
  componentInputInvalidMessagePrefix:
    "There was an issue with the phone number:",
});

const userDisplay = ref<string>("");
const inputMaskKey = ref(0);
let internallyClearingModel = false;

const clearModelInternally = () => {
  if (model.value == null) {
    internallyClearingModel = false;
    return;
  }
  internallyClearingModel = true;
  model.value = null;
};

const externalResults = reactive({ phone: [] as string[] });

const model = defineModel<string | null | NANPPhoneNumber | undefined>();

const validationState = reactive({
  phone: computed(() => (userDisplay.value || model.value || "").toString()),
});

const keepExternalResultsWired = () => true;

const rules = computed(() => ({
  phone: {
    ...(props.required && {
      required: helpers.withMessage("Phone number is required.", requiredRule),
    }),
    keepExternalResultsWired,
  },
}));

const v$ = useVuelidate(rules, validationState, {
  $externalResults: externalResults,
});

watch(
  () => model.value,
  (parentValue) => {
    if (!parentValue) {
      if (internallyClearingModel) {
        internallyClearingModel = false;
        return;
      }
      externalResults.phone = [];
      v$.value.$reset();
      if (!userDisplay.value) {
      } else {
        userDisplay.value = "";
        // PrimeVue InputMask retains a stale internal buffer when the model
        // is cleared externally and writes it back to the DOM ~10ms later.
        // Remount it so it starts with a fresh buffer.
        // https://github.com/primefaces/primevue/issues/8570
        inputMaskKey.value++;
      }
      return;
    }
    const phoneNumber = castOnValidPhoneNumber(parentValue);
    if (phoneNumber) {
      userDisplay.value = stripCountryCode(phoneNumber);
      externalResults.phone = [];
    } else {
      userDisplay.value = "";
      externalResults.phone = [
        `${props.componentInputInvalidMessagePrefix} ${parentValue}`,
      ];
      v$.value.$touch();
    }
  },
  { immediate: true }
);

const errorId = computed(() => `${props.id}-error`);

const onComplete = () => {
  if (!userDisplay.value.trim()) {
    externalResults.phone = [];
    clearModelInternally();
    return;
  }
  const phoneOrError = normalizePhone(userDisplay.value);
  if (phoneOrError instanceof Error) {
    externalResults.phone = [phoneOrError.message];
    clearModelInternally();
    return;
  }
  externalResults.phone = [];
  model.value = phoneOrError;
};

const alreadyDisplayingError = () => {
  return !!externalResults.phone.length;
};

const onBlur = () => {
  const noUserChange = !userDisplay.value.trim();
  if (noUserChange) {
    if (alreadyDisplayingError()) return;
    if (props.required) v$.value.$touch();
    externalResults.phone = [];
    clearModelInternally();
    return;
  }
  if (userDisplay.value.length < phoneNumberNoCodeLength()) {
    v$.value.$touch();
    externalResults.phone = [phoneNumberErrorMessage()];
    clearModelInternally();
    return;
  }
  const phoneOrError = normalizePhone(userDisplay.value);
  if (phoneOrError instanceof Error) {
    v$.value.$touch();
    externalResults.phone = [phoneOrError.message];
    clearModelInternally();
    return;
  }
  externalResults.phone = [];
  model.value = phoneOrError;
};
</script>
